import { SupportedGames } from "@/types/game";
import { getGameHandler } from "@/games";
import type { DeckIdentity } from "@/interfaces/DeckIdentity";
import {
  deriveArchetypeFromDecklist,
  getArchetypeDataFromAPI,
  getArchetypeFromCache,
} from "@/lib/games/palworld/archetypeManager";

/**
 * Resolve a deck's identity for any game.
 *
 * Games differ in how identity is obtained - One Piece and Naruto read a real
 * card, Union Arena parses a synthetic "<SET> <COLOR> - <Name>" id, Riftbound
 * reads its Legend - but they all answer the same question, so this delegates
 * to the game's own handler rather than switching on the game id.
 */
export function getLeaderData(
  game: SupportedGames,
  leaderId: string,
): DeckIdentity | undefined {
  return getGameHandler(game).getLeaderData(leaderId);
}

/**
 * Get deck identity with automatic Palworld archetype handling.
 *
 * This is the recommended function to use when you have access to the full decklist.
 * For Palworld, it tries to fetch archetype data from API, falls back to derivation.
 * For other games, it uses the standard leader data lookup.
 *
 * @param game - The game being played
 * @param leaderId - Leader/archetype ID from the decklist
 * @param decklistString - Full decklist string (used for Palworld fallback)
 * @returns DeckIdentity with proper archetype/leader information
 */
export function getDeckIdentity(
  game: SupportedGames,
  leaderId: string,
  decklistString?: string,
): DeckIdentity | undefined {
  // For Palworld, check cache first, then derive as fallback
  if (game === "palworld") {
    // Try to get from cache (if server pre-fetched all archetypes)
    if (leaderId) {
      const cached = getArchetypeFromCache(91, leaderId);
      if (cached) {
        return cached;
      }
    }

    // Fallback: derive from decklist if available
    if (decklistString) {
      const result = deriveArchetypeFromDecklist(decklistString);
      return result;
    }
  }

  // For other games, use the standard leader data lookup
  return getLeaderData(game, leaderId);
}

/**
 * Async version: Get deck identity with API fetch for Palworld archetypes.
 *
 * Use this in server components or async contexts where you can await.
 * For Palworld, fetches archetype data from backend API, falls back to derivation.
 * For other games, uses standard leader data lookup.
 *
 * @param game - The game being played
 * @param leaderId - Leader/archetype ID from the decklist
 * @param decklistString - Full decklist string (for Palworld API fallback)
 * @returns Promise of DeckIdentity with proper archetype/leader information
 */
export async function getDeckIdentityAsync(
  game: SupportedGames,
  leaderId: string,
  decklistString?: string,
): Promise<DeckIdentity | undefined> {
  // For Palworld, try to fetch from API first, then fall back to derivation
  if (game === "palworld") {
    if (leaderId) {
      console.log(
        "[TEST] getDeckIdentityAsync - Fetching Palworld archetype from API, leaderId:",
        leaderId,
      );
    }
    const result = await getArchetypeDataFromAPI(leaderId, decklistString, 91); // 91 = Palworld game ID
    if (leaderId && result) {
    }
    return result;
  }

  // For other games, use the standard leader data lookup
  return getLeaderData(game, leaderId);
}

/** @deprecated Use getLeaderData instead */
export function getArchetypeData(
  game: SupportedGames,
  archetypeId: string,
): DeckIdentity | undefined {
  return getLeaderData(game, archetypeId);
}
